/**
 * Portão da triagem do caso (E45.F2) — reformado na E67.F1.
 *
 * Até aqui a tela resolvia UMA contestada (`signatures.find(kind==='contested')`)
 * e disparava a triagem só para ela; um caso com 5 questionadas triava 1 e as
 * outras 4 nunca eram vistas. A leitura tinha o mesmo defeito: `pas.at(-1)`
 * mostrava a triagem mais recente, não a de cada questionada.
 *
 * Agora: uma triagem POR questionada viva, casada por `contested_signature_id`,
 * com auto-run só do que falta (chave de idempotência por questionada, para
 * reload no meio não duplicar exame) e resultado incremental — cada uma aparece
 * quando chega, sem esperar a última.
 *
 * O portão de decisão é do CASO (decisão do owner 2026-08-06): botões no rodapé
 * que aplicam a todas as triagens pendentes numa transação do servidor. Na E72.F2
 * ele ganhou a terceira saída — encerrar EMITINDO o Relatório Preliminar — e, com
 * ela, um estado pós-decisão: o portão some quando não há mais triagem pendente,
 * e é o bloco do desfecho que mantém o download ao alcance.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ptBR } from '@/i18n/pt-BR';
import { PreAnaliseTriagemItem } from '@/components/PreAnalise/PreAnaliseTriagemItem';
import { DesfechoComRelatorio, PortaoDoCaso } from '@/components/PreAnalise/PortaoDoCaso';
import { CaseStageStepper } from '@/components/Casos/CaseStageStepper';
import { useApiClient } from '@/lib/api';
import { CaseError, getCase, type CaseRecordData, type WorkflowStatus } from '@/lib/cases';
import { listSignatures, type SignatureRecordData } from '@/lib/case_signatures';
import {
  applyPreAnaliseAcaoDoCaso,
  baixarRelatorioPreliminar,
  listPreAnalises,
  PreAnaliseError,
  runPreAnalise,
  type PreAnaliseAcao,
  type PreAnaliseErrorReason,
  type PreAnaliseRecordData,
} from '@/lib/pre_analise';
import { salvarArquivoBaixado } from '@/lib/http_download';

const _ERROR_MESSAGE: Record<PreAnaliseErrorReason, string> = {
  case_not_found: ptBR.preAnalise.errorCaseNotFound,
  contested_not_found: ptBR.preAnalise.errorContestedNotFound,
  no_api_key: ptBR.preAnalise.errorNoApiKey,
  insufficient_signatures: ptBR.preAnalise.errorInsufficientSignatures,
  contract: ptBR.preAnalise.errorContract,
  assistant_unavailable: ptBR.preAnalise.errorAssistant,
  not_found: ptBR.preAnalise.errorGeneric,
  invalid_transition: ptBR.preAnalise.errorInvalidTransition,
  relatorio_not_ready: ptBR.preAnalise.errorRelatorioNotReady,
  network: ptBR.preAnalise.errorNetwork,
  unknown: ptBR.preAnalise.errorGeneric,
};

function messageFor(err: unknown): string {
  if (err instanceof PreAnaliseError) return _ERROR_MESSAGE[err.reason];
  return ptBR.preAnalise.errorGeneric;
}

/** Falha ao carregar o CASO (portão por modalidade, E74.F1). */
function messageForCase(err: unknown): string {
  if (err instanceof CaseError && err.reason === 'not_found') {
    return ptBR.preAnalise.errorCaseNotFound;
  }
  if (err instanceof CaseError && err.reason === 'network') return ptBR.preAnalise.errorNetwork;
  return ptBR.preAnalise.errorGeneric;
}

/**
 * Mensagem do DOWNLOAD (E72.F2): falha de rede ou do conversor aqui não é
 * "não foi possível concluir a análise preliminar" — a triagem já terminou, e o
 * que falhou foi preparar o arquivo. Só o 422 do gate tem texto próprio, porque
 * ele diz uma coisa diferente: este caso não tem documento.
 */
function messageForDownload(err: unknown): string {
  if (err instanceof PreAnaliseError && err.reason === 'relatorio_not_ready') {
    return ptBR.preAnalise.errorRelatorioNotReady;
  }
  return ptBR.preAnalise.errorRelatorioDownload;
}

/**
 * Chave estável por questionada (E50.F4.C02): reload no meio do portão devolve
 * o exame já criado em vez de duplicá-lo. O retry MANUAL não usa chave — re-rodar
 * a triagem cria registro novo, que é o comportamento deliberado de hoje.
 */
function chaveDoPortao(signatureId: string): string {
  return `perito-portao:${signatureId}`;
}

function vivas(signatures: SignatureRecordData[], kind: 'contested' | 'standard') {
  return signatures.filter((s) => s.kind === kind && s.deleted_at == null);
}

/**
 * Portão por modalidade (E74.F1, ADR-0075 §1): o caso decide a tela. Carrega o
 * caso UMA vez e entrega `workflow_status` à tela escolhida — a triagem visual
 * (grafotécnica) ou a pré-análise do protocolo (eletrônica).
 */
export function PreAnalisePage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const { search, hash } = useLocation();
  const client = useApiClient();
  // F-E89.F2-01: a rota troca o `:id` sem remontar esta página. Enquanto o caso
  // novo carrega, o cadastro e o erro guardados são do ANTERIOR — e entregá-los
  // montaria a tela de um caso com o id de outro (e o status, e a modalidade).
  // Cada resultado guarda o id que foi PEDIDO: comparar com o id devolvido
  // travaria a tela numa URL com o mesmo caso escrito de outro jeito.
  const [carga, setCarga] = useState<
    { caseId: string; caso: CaseRecordData } | { caseId: string; erro: string } | null
  >(null);
  const caso = carga?.caseId === caseId && 'caso' in carga ? carga.caso : null;
  const erro = carga?.caseId === caseId && 'erro' in carga ? carga.erro : null;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const record = await getCase(client, caseId);
        if (!cancelled) setCarga({ caseId, caso: record });
      } catch (err) {
        if (!cancelled) setCarga({ caseId, erro: messageForCase(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, caseId]);

  if (caso === null) {
    return (
      <section className="mx-auto max-w-5xl" data-testid="pre-analise-page">
        <h1 className="text-display-md text-fg">{ptBR.preAnalise.pageTitle}</h1>
        {erro !== null ? (
          <p
            className="mt-sc-4 border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
            role="alert"
            data-testid="pre-analise-error"
          >
            {erro}
          </p>
        ) : (
          <p className="mt-sc-6 text-body-md text-fg-muted" data-testid="pre-analise-loading">
            {ptBR.preAnalise.loading}
          </p>
        )}
      </section>
    );
  }

  // Enquanto o caso da rota carrega, o placeholder acima toma o lugar da tela da
  // modalidade: cada caso monta a sua, e nada do anterior sobrevive à troca.
  // E201.F1 (GUS-47, ADR-0144): o caso eletrônico não tem mais pré-análise própria: a mesa única
  // junta a pré-análise e a mesa. O link antigo leva para lá, com a query e a âncora.
  if (caso.modalidade === 'eletronica') {
    return <Navigate to={{ pathname: `/casos/${caseId}/mesa`, search, hash }} replace />;
  }
  return <PreAnaliseTriagemVisual caseId={caseId} workflowStatus={caso.workflow_status ?? null} />;
}

/** A triagem visual da modalidade grafotécnica — o corpo original desta tela. */
function PreAnaliseTriagemVisual({
  caseId,
  workflowStatus,
}: {
  caseId: string;
  workflowStatus: WorkflowStatus | null;
}): JSX.Element {
  const navigate = useNavigate();
  const client = useApiClient();

  const [signatures, setSignatures] = useState<SignatureRecordData[] | null>(null);
  const [preAnalises, setPreAnalises] = useState<PreAnaliseRecordData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Qual ação está em voo (E72.F2): com três CTAs, um booleano trocaria o rótulo
  // dos três ao mesmo tempo e a pessoa não saberia qual foi acionado.
  const [acaoEmCurso, setAcaoEmCurso] = useState<PreAnaliseAcao | null>(null);
  const [baixando, setBaixando] = useState(false);
  // E85.F2: o código de verificação do último relatório baixado nesta tela.
  const [codigoDoRelatorio, setCodigoDoRelatorio] = useState<string | null>(null);
  const [rodando, setRodando] = useState<string[]>([]);
  const [errosPorContestada, setErrosPorContestada] = useState<Record<string, string>>({});
  // E47.F1.T02: garante um único disparo automático por montagem — se a triagem
  // falhar, NÃO reexecuta em loop; cai no botão manual de retry.
  const autoRunAttempted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      try {
        const [sigs, pas] = await Promise.all([
          listSignatures(client, caseId),
          listPreAnalises(client, caseId),
        ]);
        if (cancelled) return;
        setSignatures(sigs);
        setPreAnalises(pas);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [client, caseId]);

  const contestadas = useMemo(
    () => (signatures === null ? [] : vivas(signatures, 'contested')),
    [signatures],
  );
  const padroes = useMemo(
    () => (signatures === null ? [] : vivas(signatures, 'standard')),
    [signatures],
  );

  /**
   * A triagem de cada questionada, casada por `contested_signature_id`. Quando a
   * mesma questionada tem mais de um registro (o retry manual cria um novo), o
   * corrente é o ÚLTIMO — a lista do servidor é cronológica.
   */
  const triagemPorContestada = useMemo(() => {
    const mapa = new Map<string, PreAnaliseRecordData>();
    for (const pa of preAnalises) mapa.set(pa.contested_signature_id, pa);
    return mapa;
  }, [preAnalises]);

  const registrarResultado = useCallback((record: PreAnaliseRecordData): void => {
    setPreAnalises((atuais) => [
      ...atuais.filter((pa) => pa.contested_signature_id !== record.contested_signature_id),
      record,
    ]);
  }, []);

  const dispararUma = useCallback(
    async (signatureId: string, options?: { idempotencyKey?: string }): Promise<void> => {
      setRodando((ids) => [...ids, signatureId]);
      setErrosPorContestada((atuais) => {
        const { [signatureId]: descartado, ...resto } = atuais;
        void descartado;
        return resto;
      });
      try {
        registrarResultado(await runPreAnalise(client, caseId, signatureId, options));
      } catch (err) {
        // Falha isolada: uma questionada que falhou não impede as outras de
        // aparecerem, e o erro fica NA linha dela, não numa faixa global.
        setErrosPorContestada((atuais) => ({ ...atuais, [signatureId]: messageFor(err) }));
      } finally {
        setRodando((ids) => ids.filter((id) => id !== signatureId));
      }
    },
    [client, caseId, registrarResultado],
  );

  // Auto-run do portão: dispara SÓ as questionadas que ainda não têm triagem,
  // uma de cada vez (o agente roda K execuções por chamada — paralelizar N
  // questionadas multiplicaria a carga sem ganho de percepção, já que cada
  // resultado aparece assim que chega).
  useEffect(() => {
    if (loading || autoRunAttempted.current) return;
    if (contestadas.length === 0 || padroes.length === 0) return;
    const faltantes = contestadas.filter((c) => !triagemPorContestada.has(c.id));
    if (faltantes.length === 0) return;
    autoRunAttempted.current = true;
    void (async () => {
      for (const contestada of faltantes) {
        await dispararUma(contestada.id, { idempotencyKey: chaveDoPortao(contestada.id) });
      }
    })();
  }, [loading, contestadas, padroes, triagemPorContestada, dispararUma]);

  const handleAcao = useCallback(
    async (acao: PreAnaliseAcao): Promise<void> => {
      setAcaoEmCurso(acao);
      setError(null);
      try {
        const atualizadas = await applyPreAnaliseAcaoDoCaso(client, caseId, acao);
        setPreAnalises((atuais) => {
          const porId = new Map(atualizadas.map((pa) => [pa.id, pa]));
          return atuais.map((pa) => porId.get(pa.id) ?? pa);
        });
        // E72.F2: gerar o relatório NÃO baixa sozinho. O documento é
        // re-renderizado sob demanda, e um download disparado por efeito colateral
        // do clique num botão chamado "gerar" some no gerenciador de downloads sem
        // a pessoa entender que ele existe e continua disponível.
        if (acao === 'seguir_mesa') navigate(`/casos/${caseId}/workspace`);
      } catch (err) {
        setError(messageFor(err));
      } finally {
        setAcaoEmCurso(null);
      }
    },
    [client, caseId, navigate],
  );

  const handleBaixarRelatorio = useCallback(async (): Promise<void> => {
    setBaixando(true);
    setError(null);
    try {
      const arquivo = await baixarRelatorioPreliminar(client, caseId);
      salvarArquivoBaixado(arquivo);
      setCodigoDoRelatorio(arquivo.codigoVerificacao ?? null);
    } catch (err) {
      setError(messageForDownload(err));
    } finally {
      setBaixando(false);
    }
  }, [client, caseId]);

  const temPendente = preAnalises.some((pa) => pa.status === 'em_triagem');
  /**
   * O caso foi encerrado COM relatório (E72.F2). A decisão é do caso e roda numa
   * transação sobre todas as triagens pendentes, então basta uma no desfecho —
   * e é `some`, não `every`, porque uma questionada retriada depois deixaria
   * registro `em_triagem` ao lado, sem apagar o documento já emitido.
   */
  const temRelatorio = preAnalises.some((pa) => pa.status === 'encerrada_com_relatorio');
  const triadas = contestadas.filter((c) => triagemPorContestada.has(c.id)).length;
  /**
   * O portão só decide o caso INTEIRO (E67.F1.C01, F-E67.F1-01). Antes ele
   * travava apenas enquanto `rodando.length > 0`: bastava uma questionada
   * falhar para a sequência terminar, o botão habilitar e o perito levar à mesa
   * um caso com metade das assinaturas sem triagem — a cobertura parcial que
   * esta Etapa foi aberta para eliminar, de volta pela porta do erro.
   *
   * A régua é cobertura, não ausência de erro: `triadas === contestadas.length`
   * já cobre o caso em que a falha foi resolvida por retry (o registro existe) e
   * o caso em que ela nunca foi (não existe). O erro entra na conta porque a
   * linha que falhou pode ter resultado ANTIGO em tela e mesmo assim precisa de
   * atenção antes da decisão.
   */
  const temErro = contestadas.some((c) => errosPorContestada[c.id] !== undefined);
  const travado = rodando.length > 0 || triadas < contestadas.length || temErro;

  return (
    <section
      className="mx-auto max-w-5xl"
      data-testid="pre-analise-page"
      aria-labelledby="pre-analise-title"
    >
      <h1 id="pre-analise-title" className="text-display-md text-fg">
        {ptBR.preAnalise.pageTitle}
      </h1>
      <p className="mt-1 max-w-prose text-body-md text-fg-muted">{ptBR.preAnalise.pageSubtitle}</p>

      <div className="mt-sc-4">
        <CaseStageStepper
          caseId={caseId}
          workflowStatus={workflowStatus}
          activeStage="preliminar"
        />
      </div>

      {/* E56.F1.T01: a faixa de ressalva que abria a tela saiu a pedido do owner.
          O rótulo não-pericial permanece onde o resultado deixa o sistema — no
          cabeçalho e no rodapé do resumo compartilhável (ADR-0056 §6). */}
      {error !== null && (
        <p
          className="mt-sc-4 border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="pre-analise-error"
        >
          {error}
        </p>
      )}

      {loading ? (
        <p className="mt-sc-6 text-body-md text-fg-muted" data-testid="pre-analise-loading">
          {ptBR.preAnalise.loading}
        </p>
      ) : contestadas.length === 0 ? (
        <p className="mt-sc-6 text-body-md text-fg-muted" data-testid="pre-analise-needs-contested">
          {ptBR.preAnalise.needsContested}
        </p>
      ) : padroes.length === 0 ? (
        <p className="mt-sc-6 text-body-md text-fg-muted" data-testid="pre-analise-needs-pattern">
          {ptBR.preAnalise.needsPattern}
        </p>
      ) : (
        <>
          <div className="mt-sc-6 space-y-sc-8" data-testid="pre-analise-lista">
            {contestadas.map((contestada, indice) => (
              <PreAnaliseTriagemItem
                key={contestada.id}
                caseId={caseId}
                contestada={contestada}
                padroes={padroes}
                preAnalise={triagemPorContestada.get(contestada.id) ?? null}
                ordinal={ptBR.preAnalise.contestadaOrdinal
                  .replace('{n}', String(indice + 1))
                  .replace('{total}', String(contestadas.length))}
                erro={errosPorContestada[contestada.id] ?? null}
                rodando={rodando.includes(contestada.id)}
                busy={acaoEmCurso !== null}
                onRetry={() => void dispararUma(contestada.id)}
              />
            ))}
          </div>

          <PortaoDoCaso
            visivel={temPendente}
            triadas={triadas}
            total={contestadas.length}
            acaoEmCurso={acaoEmCurso}
            travado={travado}
            rodando={rodando.length > 0}
            onAcao={(acao) => void handleAcao(acao)}
          />

          <DesfechoComRelatorio
            visivel={!temPendente && temRelatorio}
            baixando={baixando}
            codigoDoArquivo={codigoDoRelatorio}
            onBaixar={() => void handleBaixarRelatorio()}
          />

          {rodando.length > 0 && (
            <p
              className="mt-sc-4 flex items-center gap-sc-2 text-body-sm text-fg-muted"
              role="status"
              aria-live="polite"
              data-testid="pre-analise-running"
            >
              <Loader2 className="h-4 w-4 animate-spin text-accent" aria-hidden />
              {ptBR.preAnalise.running}
            </p>
          )}
        </>
      )}
    </section>
  );
}
